import type { ShareRole } from "@zapier/bff";
import {
  type HydratedAssetShare,
  type Member,
  useFetchAssetShares,
} from "@zapier/client";
import { PermissionAssetType } from "@zapier/bff";

import styles from "./ShareList.module.css";
import { ShareListItem, ShareListItemLoading } from "./ShareListItem";
import { OwnerListItem } from "./OwnerListItem";

interface Props {
  isDisabled?: boolean;
  assetId: string;
  assetOwner?: Member;
  assetType: PermissionAssetType;
  roles: ShareRole[];
  onChangeOwner?: (newOwner: Member) => void;
  canChangeOwner?: boolean;
}

function shareKey(share: HydratedAssetShare): string {
  const subjectType =
    share.group === "admin" ? "accountadmin" : share.subjectType;
  return `${subjectType}:${share.subjectId}`;
}

export function ShareList({
  isDisabled,
  assetId,
  assetOwner,
  assetType,
  roles,
  onChangeOwner,
  canChangeOwner,
}: Props) {
  const { sharesByType, isLoading: isLoadingShares } = useFetchAssetShares(
    { assetId, assetType },
    { enabled: Boolean(assetId) },
  );

  return (
    <ul className={styles.list} data-testid="share-list">
      {/* 1. Account-wide sharing */}
      {sharesByType.account?.map((share) => (
        <ShareListItem
          isDisabled={isDisabled}
          key={shareKey(share)}
          roles={roles}
          share={share}
        />
      ))}

      {/* 2. Owner information */}
      <OwnerListItem owner={assetOwner} />

      {isLoadingShares && (
        <>
          <ShareListItemLoading />
          <ShareListItemLoading />
          <ShareListItemLoading />
        </>
      )}

      {/* 3. Team shares */}
      {sharesByType.team?.map((share) => (
        <ShareListItem
          isDisabled={isDisabled}
          key={shareKey(share)}
          roles={roles}
          share={share}
        />
      ))}

      {/* 4. User shares */}
      {sharesByType.user?.map((share) => (
        <ShareListItem
          isDisabled={isDisabled}
          key={shareKey(share)}
          roles={roles}
          share={share}
          onChangeOwner={onChangeOwner}
          canChangeOwner={canChangeOwner}
        />
      ))}
    </ul>
  );
}
